import { useState } from "react";
import { CtaLink } from "../ui/CtaLink";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "../i18n";

const items = [
  {
    q: "How is my personalized plan created?",
    a: "Our AI analyzes your test results, symptoms, goals and medical history. It cross-references this with Swiss nutritional research and physician-validated protocols to generate a plan tailored to you.",
  },
  {
    q: "Do you use real lab results?",
    a: "Yes. You upload your bloodwork (PDF or photo), and we parse biomarkers to drive dosage and timing decisions.",
  },
  {
    q: "Who supervises the recommendations?",
    a: "Licensed nutritionists and physicians review the AI protocols. You can also request a 1:1 consultation.",
  },
  {
    q: "How is my data protected?",
    a: "End-to-end encrypted, stored in Switzerland, never sold. You can delete everything at any time.",
  },
  {
    q: "When can I start?",
    a: "We are in private beta. Join the waitlist to get early access and founding-member pricing.",
  },
];

export function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  const { t } = useI18n();

  return (
    <section className="py-28 bg-[var(--color-cream)]">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-5xl">{t("faq.title")}</h2>
        <p className="mt-3 text-[var(--color-muted)]">{t("faq.sub")}</p>
      </div>

      <div className="max-w-3xl mx-auto mt-14 px-6">
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div key={i} className="border-b border-[var(--color-ink)]/15">
              <button
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full flex items-center justify-between py-6 text-left"
              >
                <span className="font-serif text-2xl text-[var(--color-ink)]">{it.q}</span>
                <motion.span
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  className="text-[var(--color-muted)] text-xl"
                >
                  ⌄
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 pr-12 text-[var(--color-muted)] leading-relaxed">{it.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        <div className="mt-14 text-center">
          <p className="text-[var(--color-muted)] mb-4">
            Still wondering? Share your context in a 2-minute survey.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <CtaLink cta="survey" block="faq" to="/survey" className="pill-btn">{t("hero.cta.survey")}</CtaLink>
            <CtaLink cta="waitlist" block="faq" to="/waitlist" className="pill-outline">{t("hero.cta.join")}</CtaLink>
          </div>
        </div>
      </div>
    </section>
  );
}
